import { STATUS_LABELS } from "@/lib/roster";

/** Format a dollar amount as "$X.XX" */
export function formatCurrency(dollars: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(dollars);
}

/** Dollars for running copy: "$20" for a whole amount, "$19.50" otherwise. The
 *  copy interpolates configured prices, so it cannot assume whole dollars. */
export function formatDollars(dollars: number): string {
  return Number.isInteger(dollars) ? `$${dollars}` : formatCurrency(dollars);
}

/** Format an ISO date string as "Feb 25, 2026" */
export function formatDate(isoString: string): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

/** Format an ISO instant as "Oct 5, 2026, 2:30 PM", in the viewer's time zone. */
export function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

/** Format order status to human-readable label */
export function formatOrderStatus(status: string): string {
  const labels: Record<string, string> = {
    paid: "Paid",
    processing: "Processing",
    completed: "Completed",
  };
  return labels[status] ?? status;
}

/** Format employee status to human-readable label. The wording lives in
 *  STATUS_LABELS (roster.ts) so no screen shows two wordings for one status. */
export function formatEmployeeStatus(status: string): string {
  return (STATUS_LABELS as Record<string, string>)[status] ?? status;
}

/** Format payment method to human-readable label */
export function formatPaymentMethod(method: string): string {
  const labels: Record<string, string> = {
    credit_card: "Credit Card",
    ach: "ACH",
  };
  return labels[method] ?? method;
}

/** Format a US phone number as "(850) 555-1234", as far as the digits go */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days from `iso` to `now`; a future instant counts as 0. */
export function daysSince(iso: string, now: Date = new Date()): number {
  const ms = now.getTime() - Date.parse(iso);
  return Number.isNaN(ms) || ms < 0 ? 0 : Math.floor(ms / DAY_MS);
}

/** "today", "1 day ago", "50 days ago". */
export function formatDaysAgo(iso: string, now: Date = new Date()): string {
  const days = daysSince(iso, now);
  if (days === 0) return "today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/** A queue card's age: "waiting since today", "waiting 1 day", "waiting 50 days". */
export function formatWaiting(iso: string, now: Date = new Date()): string {
  const days = daysSince(iso, now);
  if (days === 0) return "waiting since today";
  return days === 1 ? "waiting 1 day" : `waiting ${days} days`;
}
